import React, { useState, useEffect, useCallback } from 'react';
import { Text, ScrollView, View, TouchableOpacity, Pressable, ActivityIndicator } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import * as ImagePicker from 'expo-image-picker';
import { Image } from 'expo-image';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { saveFile } from '@/utils/FileManager';
import Input from '@/components/General/inputs/Input';
import { timezonesByLocation, timezonesByName } from '@/utils/constants/timezones';
import SelectInput from '@/components/General/inputs/SelectInput';
import Toggle from '@/components/General/inputs/Toggle';

import { saveOrganization } from '@/data/organization';
import * as Sentry from '@sentry/react-native';
import SolidButton from '@/components/General/SolidButton';
import { createNotification } from '@/data/notifications';
import { supabase } from '@/data/client';
import { impactMedium } from '@/utils/haptic';
import Toast from 'react-native-toast-message';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';

let timer = null

const SettingRow = ({ icon, label, onPress, right, isFirst, isLast, destructive }) => {
    const {theme: {colors}} = useUnistyles();
    return (
        <TouchableOpacity
            activeOpacity={onPress ? 0.6 : 1}
            onPress={onPress}
            style={[
                styles.row,
                isFirst && styles.rowFirst,
                isLast && styles.rowLast,
                !isLast && styles.rowBorder,
            ]}
        >
            <View style={styles.rowLeft}>
                <View style={[styles.iconCircle, destructive && styles.iconCircleDestructive]}>
                    <Unicon name={icon} size={18} colorVariant={destructive ? "error" : "primary"} />
                </View>
                <Text style={[styles.rowLabel, destructive && {color: colors.error}]}>{label}</Text>
            </View>
            {right || (onPress && <Unicon name="chevron-forward" size={18} colorVariant="textTertiary" />)}
        </TouchableOpacity>
    );
};

const SettingBlock = ({ icon, title, description, children, isFirst, isLast }) => (
    <View style={[styles.blockRow, isFirst && styles.rowFirst, isLast && styles.rowLast, !isLast && styles.rowBorder]}>
        <View style={styles.blockHeader}>
            <View style={styles.iconCircle}>
                <Unicon name={icon} size={18} colorVariant="primary" />
            </View>
            <View style={{flex: 1}}>
                <Text style={styles.rowLabel}>{title}</Text>
                {description ? <Text style={styles.blockDescription}>{description}</Text> : null}
            </View>
        </View>
        <View style={styles.blockContent}>
            {children}
        </View>
    </View>
);

export default function OrgSettingsScreen() {
    /*** State ***/
    const [ currentSettings, setCurrentSettings ] = useState({})
    const [weeklySummaryEmailInput, setWeeklySummaryEmailInput] = useState('')
    // Which LOGO_CONFIG key is mid-upload (null = idle).
    const [uploadingLogo, setUploadingLogo] = useState(null)
    // Stable-per-mount cache buster for logo previews; bumped after each upload
    // so a re-uploaded image at the same storage URL actually refreshes.
    const [logoCacheBust, setLogoCacheBust] = useState(() => Date.now())


    /*** Queries ***/
    const router = useRouter()
    const queryClient = useQueryClient()
    const { entitlements } = useOrganizationEntitlements();
    // File storage is a paid feature — don't mount its query on Free.
    const isFree = isFreeOrg(entitlements);
    const { data: loggedInUser } = Query.people.loggedInUser()
    const { data: organization, isFetching: loadingOrganization } = Query.organization.byId({id: loggedInUser?.organizationID})
    const { data: storageUsage } = Query.files.storageUsage({ options: { enabled: !isFree } })
    const saveOrganizationMutation = useMutation({mutationFn: saveOrganization, onSuccess: () => queryClient.invalidateQueries({queryKey: QueryKeys.organization.all()})});

    const usedGB = storageUsage ? (storageUsage.usedBytes / (1024 * 1024 * 1024)).toFixed(2) : '0.00';
    const totalGB = 10;
    const progressPercent = Math.min(((storageUsage?.usedBytes || 0) / (storageUsage?.limitBytes || 1)) * 100, 100);

    
    /*** Effects ***/
    useEffect(() => {
        if(timer){
            clearTimeout(timer)
        }

        timer = setTimeout(() => {
            if(JSON.stringify(organization) != JSON.stringify(currentSettings)){
                saveOrganizationMutation.mutate(currentSettings);
            }
        }, 3000)
    }, [currentSettings]);


    useEffect(() => {
        if(!loadingOrganization){
            setCurrentSettings(organization);
        }
    }, [loadingOrganization])


    const LOGO_CONFIG = {
        logoPublicUrl: {
            label: "Logo",
            description: "Light neutral colored square icon used for app icon and emails on a darker background.",
            filename: "logo.jpg",
            aspect: [1, 1],
        },
        logoPublicUrlDark: {
            label: "Logo (Colored)",
            description: "Colored square icon used for app icon and emails on a lighter background.",
            filename: "logo-dark.jpg",
            aspect: [1, 1],
        },
        logoPublicUrlLong: {
            label: "Wide Logo",
            description: "Horizontal/wide neutral colored logo used for headers and emails on a darker background.",
            filename: "logo-long.jpg",
            aspect: [3, 1],
        },
        logoPublicUrlLongDark: {
            label: "Wide Logo (Colored)",
            description: "Horizontal/wide colored version used for headers and emails on a lighter background.",
            filename: "logo-long-dark.jpg",
            aspect: [3, 1],
        },
    };

    const choosePhoto = async (logoKey) => {
        const config = LOGO_CONFIG[logoKey];
        if (!config || !organization?.id || uploadingLogo) return;
        let result = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ['images'],
            allowsEditing: true,
            aspect: config.aspect,
            quality: 0.5,
        });

        if (!(result.assets && result.assets.length > 0)) return;

        setUploadingLogo(logoKey);
        try {
            const uploaded = await saveFile(result.assets[0].uri, `org_logos/${organization.id}/${config.filename}`, true);
            if (!uploaded?.storageUrl) {
                Toast.show({type: 'error', text2: 'Something went wrong uploading that image. Please try again.'});
                return;
            }
            // Reflect the new logo immediately — the org refetch will confirm it.
            setCurrentSettings(previous => ({...previous, [logoKey]: uploaded.storageUrl}));
            setLogoCacheBust(Date.now());
            saveOrganizationMutation.mutate({[logoKey]: uploaded.storageUrl}, {
                onSuccess: () => Toast.show({type: 'success', text2: `${config.label} updated.`}),
                onError: () => Toast.show({type: 'error', text2: 'The image uploaded but saving it failed. Please try again.'}),
            });
        }
        catch {
            Toast.show({type: 'error', text2: 'Something went wrong uploading that image. Please try again.'});
        }
        finally {
            setUploadingLogo(null);
        }
    };


    const sendTestNotification = async (type) => {
        let query;
        let path;

        impactMedium()

        switch(type){
            case "MESSAGE_RECEIVED":
                query = await supabase.from("Conversations").select("id").order("createdAt", { ascending: false }).limit(1).maybeSingle();
                path = `connect/conversations/${query?.data?.id}`;
                break;
            case "NEW_COMMENT":
                query = await supabase.from("Posts").select("id, boardID").order("createdAt", { ascending: false }).limit(1).maybeSingle();
                path = `connect/boards/${query?.data?.boardID}/posts/${query?.data?.id}`;
                break;
            case "NEW_POST":
                query = await supabase.from("Boards").select("id").order("createdAt", {ascending: false}).limit(1).maybeSingle();
                path = `connect/boards/${query?.data?.id}/feed`;
                break;
            case "TASK_ASSIGNED":
            case "TASK_RESPONSE":
                query = await supabase.from("TaskInstances").select("id").order("createdAt", {ascending: false}).limit(1).maybeSingle();
                path = `tasks/instances/${query?.data?.id}`;
                break;
        }
        
        createNotification({
            title: type + " Test Notification!",
            message: "This is a test notification for " + type, 
            type: type,
            parameters: query?.data ? {id: query?.data?.id} : {}, 
            organizationID: organization?.id,
            targetPersonID: loggedInUser?.id,
            path: path
        })
    }

    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    const addWeeklySummaryEmail = useCallback(() => {
        const email = weeklySummaryEmailInput.trim().toLowerCase()
        if (!EMAIL_REGEX.test(email)) {
            Toast.show({ type: 'error', text2: 'Please enter a valid email address.' })
            return
        }
        const existing = currentSettings?.weeklySummaryEmails || []
        if (existing.includes(email) || email === currentSettings?.primaryContactEmail?.toLowerCase()) {
            Toast.show({ type: 'error', text2: 'This email is already in the list.' })
            return
        }
        impactMedium()
        setCurrentSettings({
            ...currentSettings,
            weeklySummaryEmails: [...existing, email],
        })
        setWeeklySummaryEmailInput('')
    }, [weeklySummaryEmailInput, currentSettings])

    const removeWeeklySummaryEmail = useCallback((emailToRemove) => {
        impactMedium()
        setCurrentSettings({
            ...currentSettings,
            weeklySummaryEmails: (currentSettings?.weeklySummaryEmails || []).filter(e => e !== emailToRemove),
        })
    }, [currentSettings])

    const timezoneOptions = Object.keys(timezonesByName);

    return (
        <View style={{flex: 1}}>
            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled"
>
                <View style={styles.container}>

                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Branding</Text>
                        <View style={styles.card}>
                            <SettingBlock
                                icon="business-outline"
                                title="Organization Name"
                                description="Displayed in app, emails, notifications, and other communications."
                                isFirst
                            >
                                <Input
                                    key="name"
                                    title="Name"
                                    value={currentSettings?.name}
                                    onUpdate={(new_value) => {
                                        setCurrentSettings({
                                            ...currentSettings,
                                            name: new_value
                                        })
                                    }}
                                />
                            </SettingBlock>

                            <SettingRow
                                icon="at-outline"
                                label="Handle"
                                right={<Text style={styles.valueText}>{currentSettings?.handle}</Text>}
                            />

                            {Object.entries(LOGO_CONFIG).map(([key, config], index) => {
                                const isLast = index === Object.entries(LOGO_CONFIG).length - 1;
                                const isWide = config.aspect[0] > config.aspect[1];
                                const url = currentSettings?.[key];
                                return (
                                    <View
                                        key={key}
                                        style={[styles.blockRow, !isLast && styles.rowBorder, isLast && styles.rowLast]}
                                    >
                                        <View style={styles.blockHeader}>
                                            <View style={styles.iconCircle}>
                                                <Unicon name="image-outline" size={18} colorVariant="primary" />
                                            </View>
                                            <View style={{flex: 1}}>
                                                <Text style={styles.rowLabel}>{config.label}</Text>
                                                <Text style={styles.blockDescription}>{config.description}</Text>
                                            </View>
                                        </View>
                                        <View style={styles.logoPreviewWrap}>
                                            <Pressable testID={`organization-logo-${key}`} accessibilityRole="button"
                                                accessibilityLabel={`Upload ${config.label}`} disabled={!!uploadingLogo} onPress={() => choosePhoto(key)}>
                                                {uploadingLogo === key ? (
                                                    <View style={[isWide ? styles.logoPreviewWide : styles.logoPreviewSquare, styles.logoPlaceholder]}>
                                                        <ActivityIndicator />
                                                    </View>
                                                ) : url ? (
                                                    <Image
                                                        source={{uri: url + '?t=' + logoCacheBust}}
                                                        style={isWide ? styles.logoPreviewWide : styles.logoPreviewSquare}
                                                        contentFit="contain"
                                                    />
                                                ) : (
                                                    <View style={[isWide ? styles.logoPreviewWide : styles.logoPreviewSquare, styles.logoPlaceholder]}>
                                                        <Unicon name="cloud-upload-outline" size={20} colorVariant="textTertiary" />
                                                    </View>
                                                )}
                                            </Pressable>
                                            <Pressable testID={`logo-upload-${key}`} disabled={!!uploadingLogo} onPress={() => choosePhoto(key)} style={styles.logoUploadButton(!!uploadingLogo)}>
                                                <Text style={styles.logoUploadText}>{uploadingLogo === key ? "Uploading..." : url ? "Change Image" : "Upload"}</Text>
                                            </Pressable>
                                        </View>
                                    </View>
                                );
                            })}
                        </View>
                    </View>

                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Configuration</Text>
                        <View style={styles.card}>
                            <SettingBlock
                                icon="time-outline"
                                title="Time Zone"
                                description="Used for date calculations within the app."
                                isFirst
                            >
                                <SelectInput
                                    title="Time Zone"
                                    value={timezonesByLocation[currentSettings?.timezone]}
                                    options={timezoneOptions}
                                    onUpdate={(new_value) => {
                                        setCurrentSettings({
                                            ...currentSettings,
                                            timezone: timezonesByName[new_value]
                                        })
                                    }}
                                />
                            </SettingBlock>

                            <View style={[styles.blockRow, styles.rowLast]}>
                                <View style={styles.blockHeader}>
                                    <View style={styles.iconCircle}>
                                        <Unicon name="chatbubble-ellipses-outline" size={18} colorVariant="primary" />
                                    </View>
                                    <View style={{flex: 1}}>
                                        <Text style={styles.rowLabel}>SMS Texting</Text>
                                        <Text style={styles.blockDescription}>Enable organization-wide SMS sending from Reach. Messages are billed at 1.5¢ per message segment (160 characters).</Text>
                                    </View>
                                    <Toggle
                                        value={!!currentSettings?.enableSmsSending}
                                        onValueChange={(value) => {
                                            impactMedium()
                                            setCurrentSettings({
                                                ...currentSettings,
                                                enableSmsSending: value,
                                            })
                                        }}
                                    />
                                </View>
                            </View>

                            {/* <SettingBlock
                                icon="logo-youtube"
                                title="YouTube Integration"
                                isLast
                            >
                                <Input
                                    title="API Key"
                                    secureTextEntry={true}
                                    allowSecureToggle={false}
                                    helpText="Your YouTube API key that will be used to query your channel and find video archives to link to events. To keep your account secure, you will not be able to see the value after it is saved."
                                    onUpdate={(newVal) => {
                                        setCurrentSettings({
                                            ...currentSettings,
                                            youtubeApiKey: newVal
                                        })
                                    }}
                                    value={currentSettings?.youtubeApiKey}
                                />
                                <Text style={styles.linkText} onPress={() => {
                                    if(isWeb){
                                        window.open("https://developers.google.com/youtube/registering_an_application", "_blank");
                                    }else{
                                        router.push({pathname: "(utilities)/webview", params: {url: "https://developers.google.com/youtube/registering_an_application"}})
                                    }
                                }}>How do I create a YouTube API key?</Text>
                            </SettingBlock> */}
                        </View>
                    </View>

                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Weekly Summary Email</Text>
                        <View style={styles.card}>
                            <View style={[styles.blockRow, styles.rowFirst, currentSettings?.weeklySummaryEnabled ? styles.rowBorder : styles.rowLast]}>
                                <View style={styles.blockHeader}>
                                    <View style={styles.iconCircle}>
                                        <Unicon name="mail-outline" size={18} colorVariant="primary" />
                                    </View>
                                    <View style={{flex: 1}}>
                                        <Text style={styles.rowLabel}>Enable Weekly Summary</Text>
                                        <Text style={styles.blockDescription}>Send a weekly attendance report every Monday with stats from the previous week.</Text>
                                    </View>
                                    <Toggle
                                        value={!!currentSettings?.weeklySummaryEnabled}
                                        onValueChange={(value) => {
                                            impactMedium()
                                            setCurrentSettings({
                                                ...currentSettings,
                                                weeklySummaryEnabled: value,
                                            })
                                        }}
                                    />
                                </View>
                            </View>

                            {!!currentSettings?.weeklySummaryEnabled && (
                                <View style={[styles.blockRow, styles.rowLast]}>
                                    <View style={styles.blockHeader}>
                                        <View style={styles.iconCircle}>
                                            <Unicon name="people-outline" size={18} colorVariant="primary" />
                                        </View>
                                        <View style={{flex: 1}}>
                                            <Text style={styles.rowLabel}>Recipients</Text>
                                            <Text style={styles.blockDescription}>The primary contact email is always included.</Text>
                                        </View>
                                    </View>
                                    <View style={styles.blockContent}>
                                        <View style={styles.emailChip}>
                                            <Unicon name="lock-closed-outline" size={14} colorVariant="textTertiary" />
                                            <Text style={styles.emailChipText}>{currentSettings?.primaryContactEmail}</Text>
                                        </View>

                                        {(currentSettings?.weeklySummaryEmails || []).map((email) => (
                                            <View key={email} style={styles.emailChip}>
                                                <Text style={styles.emailChipText}>{email}</Text>
                                                <TouchableOpacity onPress={() => removeWeeklySummaryEmail(email)} hitSlop={8}>
                                                    <Unicon name="close-outline" size={16} colorVariant="error" />
                                                </TouchableOpacity>
                                            </View>
                                        ))}

                                        <View style={styles.emailInputRow}>
                                            <View style={{flex: 1}}>
                                                <Input
                                                    title="Add email"
                                                    value={weeklySummaryEmailInput}
                                                    onUpdate={setWeeklySummaryEmailInput}
                                                    keyboardType="email-address"
                                                    autoCapitalize="none"
                                                />
                                            </View>
                                            <TouchableOpacity
                                                onPress={addWeeklySummaryEmail}
                                                style={styles.addEmailButton}
                                                activeOpacity={0.7}
                                            >
                                                <Unicon name="add-outline" size={20} colorVariant="primary" />
                                            </TouchableOpacity>
                                        </View>
                                    </View>
                                </View>
                            )}
                        </View>
                    </View>

                    {!isFree && (
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>File Storage</Text>
                            <View style={styles.card}>
                                <View style={[styles.blockRow, styles.rowFirst, styles.rowLast]}>
                                    <View style={styles.blockHeader}>
                                        <View style={styles.iconCircle}>
                                            <Unicon name="cloud-outline" size={18} colorVariant="primary" />
                                        </View>
                                        <View style={{flex: 1}}>
                                            <Text style={styles.rowLabel}>File Storage</Text>
                                            <Text style={styles.blockDescription}>Storage used by files uploaded to your organization.</Text>
                                        </View>
                                    </View>
                                    <View style={styles.storageContent}>
                                        <View style={styles.storageInfoRow}>
                                            <Text style={styles.storageLabel}>Storage Usage</Text>
                                            <Text style={styles.storageValue}>{usedGB} GB / {totalGB} GB</Text>
                                        </View>
                                        <View style={styles.storageProgressBar}>
                                            <View style={[styles.storageProgressFill, { width: `${progressPercent}%` }]} />
                                        </View>
                                    </View>
                                </View>
                            </View>
                        </View>
                    )}

                    {loggedInUser?.fullName == "Jeremy Sharp" &&
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Debug</Text>
                            <View style={styles.card}>
                                <SettingRow
                                    icon="bug-outline"
                                    label="TEST SENTRY ERROR"
                                    destructive
                                    isFirst
                                    onPress={() => Sentry.captureException(new Error("TEST SENTRY ERROR"))}
                                />
                                <View style={[styles.blockRow, styles.rowLast]}>
                                    <View style={styles.blockHeader}>
                                        <View style={styles.iconCircle}>
                                            <Unicon name="notifications-outline" size={18} colorVariant="primary" />
                                        </View>
                                        <Text style={styles.rowLabel}>Test Notifications</Text>
                                    </View>
                                    <View style={styles.debugButtons}>
                                        <SolidButton size='sm' text="MESSAGE_RECEIVED" onPress={() => sendTestNotification("MESSAGE_RECEIVED")} />
                                        <SolidButton size='sm' text="NEW_COMMENT" onPress={() => sendTestNotification("NEW_COMMENT")} />
                                        <SolidButton size='sm' text="NEW_POST" onPress={() => sendTestNotification("NEW_POST")} />
                                        <SolidButton size='sm' text="TASK_ASSIGNED" onPress={() => sendTestNotification("TASK_ASSIGNED")} />
                                        <SolidButton size='sm' text="TASK_RESPONSE" onPress={() => sendTestNotification("TASK_RESPONSE")} />
                                    </View>
                                </View>
                            </View>
                        </View>
                    }

                    <Pressable
                        testID="organization-setup-link"
                        accessibilityRole="link"
                        style={styles.setupLink}
                        onPress={() => router.push('/settings/organization/setup')}
                    >
                        <Unicon name="cogs" library="fa5" size={13} colorVariant="textTertiary" />
                        <Text style={styles.setupLinkText}>Organization Setup Progress</Text>
                        <Unicon name="chevron-forward" size={13} colorVariant="textTertiary" />
                    </Pressable>

                </View>
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create(({colors, fonts, spacing}) => ({
    scrollContent: {
        paddingBottom: 40,
        paddingHorizontal: spacing.md,
        alignItems: "center",
    },
    container: {
        maxWidth: 600,
        width: "100%",
    },
    section: {
        marginTop: spacing.lg,
    },
    sectionTitle: {
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        marginBottom: spacing.sm,
        marginLeft: 4,
    },
    card: {
        backgroundColor: colors.surface,
        borderRadius: 8,
        overflow: "hidden",
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: 14,
        paddingHorizontal: spacing.md,
    },
    rowFirst: {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
    },
    rowLast: {
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
    },
    rowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: colors.background,
    },
    rowLeft: {
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
        gap: 12,
    },
    iconCircle: {
        width: 34,
        height: 34,
        borderRadius: 8,
        backgroundColor: colors.primaryContainer,
        alignItems: "center",
        justifyContent: "center",
    },
    iconCircleDestructive: {
        backgroundColor: colors.errorContainer,
    },
    rowLabel: {
        fontFamily: fonts.bold,
        fontSize: 15,
        color: colors.text,
    },
    valueText: {
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.textSecondary,
    },
    linkText: {
        color: colors.info,
        fontFamily: fonts.body,
        fontSize: 12,
        marginTop: 4,
    },
    setupLink: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        alignSelf: "center",
        gap: 6,
        marginTop: spacing.lg,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.md,
    },
    setupLinkText: {
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.textSecondary,
    },
    blockRow: {
        paddingVertical: 14,
        paddingHorizontal: spacing.md,
    },
    blockHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
    },
    blockDescription: {
        fontFamily: fonts.body,
        fontSize: 12,
        color: colors.textTertiary,
        marginTop: 2,
    },
    blockContent: {
        marginTop: spacing.sm,
        marginLeft: 46,
    },
    debugButtons: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        paddingTop: spacing.sm,
        paddingLeft: 46,
        gap: 6,
    },
    logoPreviewWrap: {
        marginTop: spacing.sm,
        marginLeft: 46,
        alignItems: "flex-start",
    },
    logoPreviewSquare: {
        width: 80,
        height: 80,
        borderRadius: 8,
        backgroundColor: colors.surfaceVariant,
    },
    logoPreviewWide: {
        width: 200,
        height: 66,
        borderRadius: 8,
        backgroundColor: colors.surfaceVariant,
    },
    logoPlaceholder: {
        alignItems: "center",
        justifyContent: "center",
    },
    logoUploadButton: (disabled) => ({
        marginTop: 8,
        alignSelf: "flex-start",
        opacity: disabled ? 0.5 : 1,
        _web: {
            cursor: disabled ? "not-allowed" : "pointer",
        },
    }),
    logoUploadText: {
        fontFamily: fonts.bold,
        fontSize: 13,
        color: colors.primary,
    },
    emailChip: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: colors.surfaceVariant,
        borderRadius: 8,
        paddingVertical: 8,
        paddingHorizontal: 12,
        marginBottom: 6,
        gap: 8,
    },
    emailChipText: {
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.text,
        flex: 1,
    },
    emailInputRow: {
        flexDirection: "row",
        alignItems: "flex-end",
        gap: 8,
        marginTop: 4,
    },
    addEmailButton: {
        width: 40,
        height: 40,
        borderRadius: 8,
        backgroundColor: colors.primaryContainer,
        alignItems: "center",
    },
    storageContent: {
        marginLeft: 46,
    },
    storageInfoRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 8,
    },
    storageLabel: {
        fontFamily: fonts.body,
        fontSize: 12,
        color: colors.textSecondary,
    },
    storageValue: {
        fontFamily: fonts.bold,
        fontSize: 13,
        color: colors.text,
    },
    storageProgressBar: {
        height: 8,
        backgroundColor: colors.surfaceVariant,
        borderRadius: 4,
        overflow: "hidden",
        marginBottom: 8,
    },
    storageProgressFill: {
        height: "100%",
        backgroundColor: colors.primary,
        borderRadius: 4,
    },
}))